// Centralized registry of authoritative third-party resources used for
// outbound educational linking across the site.
//
// Shrink Network site URLs and Dr. Refai's identity/profile URLs live in
// src/lib/seo.ts (the SEO source of truth). This file holds only the
// high-domain-authority external references (NIH, NIMH, MedlinePlus, SAMHSA)
// and crisis resources, so outbound links stay consistent and maintainable.

export interface ExternalResource {
  /** Human-readable label shown to readers and used as link text. */
  name: string;
  /** Canonical destination URL. */
  url: string;
}

/** 988 Suicide & Crisis Lifeline, referenced in every crisis disclosure. */
export const CRISIS_LIFELINE: ExternalResource = {
  name: "988 Suicide & Crisis Lifeline",
  url: "https://988lifeline.org",
};

/** SAMHSA National Helpline for treatment referral and information. */
export const SAMHSA_HELPLINE: ExternalResource = {
  name: "SAMHSA National Helpline",
  url: "https://www.samhsa.gov/find-help/national-helpline",
};

/**
 * High-authority government and public-health references, keyed by topic for
 * use in article "Further Reading" blocks and resource lists.
 */
export const AUTHORITATIVE_RESOURCES = {
  nimhAnxiety: {
    name: "National Institute of Mental Health: Anxiety Disorders",
    url: "https://www.nimh.nih.gov/health/topics/anxiety-disorders",
  },
  nimhCaringMentalHealth: {
    name: "National Institute of Mental Health: Caring for Your Mental Health",
    url: "https://www.nimh.nih.gov/health/topics/caring-for-your-mental-health",
  },
  nimhTechnology: {
    name: "National Institute of Mental Health: Technology and the Future of Mental Health Treatment",
    url: "https://www.nimh.nih.gov/health/topics/technology-and-the-future-of-mental-health-treatment",
  },
  nimhStress: {
    name: "National Institute of Mental Health: I'm So Stressed Out",
    url: "https://www.nimh.nih.gov/health/publications/so-stressed-out-fact-sheet",
  },
  nimhTopics: {
    name: "National Institute of Mental Health: Health Topics",
    url: "https://www.nimh.nih.gov/health/topics",
  },
  medlineAnxiety: {
    name: "MedlinePlus: Anxiety",
    url: "https://medlineplus.gov/anxiety.html",
  },
  medlineSleep: {
    name: "MedlinePlus: Healthy Sleep",
    url: "https://medlineplus.gov/healthysleep.html",
  },
  medlineStress: {
    name: "MedlinePlus: Stress",
    url: "https://medlineplus.gov/stress.html",
  },
  medlineMentalHealth: {
    name: "MedlinePlus: Mental Health",
    url: "https://medlineplus.gov/mentalhealth.html",
  },
} as const satisfies Record<string, ExternalResource>;
